import { TagsNetworkFilterView } from '../view/TagsNetworkFilterView';
import { templateTagsNetworkFilter } from '../view/template';
import { TagsNetworkFilterRepository } from '../repository/TagsNetworkFilterRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { TagsNetworkFilterWebComponent } from '..';
import { TagItem } from '../../tagsManagerGrid/types';
import { EnumListEvents, ItemType } from '../../../ui/gzEnumList/types';
import { ExcludedTagTypes } from '../../ui/gzTagsEnumList/types';
import { LabelWidthValues } from '../../../types/common/types';

export class TagsNetworkFilterController extends BaseWebComponentController {
  public component: TagsNetworkFilterWebComponent;
  public dataRepository: TagsNetworkFilterRepository;
  public view: TagsNetworkFilterView;
  public viewConstructor: typeof TagsNetworkFilterView;
  public dataRepositoryConstructor: typeof TagsNetworkFilterRepository;
  private isExternalRender = false;

  constructor(component: TagsNetworkFilterWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: TagsNetworkFilterView,
      viewTemplate: templateTagsNetworkFilter,
      dataRepositoryConstructor: TagsNetworkFilterRepository,
    });
  }

  private selectedItemsData: any[];

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$tagFilterSelector,
        event: EnumListEvents.SELECTION_CHANGE,
        callback: this.onSelectionChangedEvent,
      },
      {
        element: this.view.dom.$tagFilterSelector,
        event: 'open',
        callback: this.onTagFilterSelectorOpen,
      },
      {
        element: this.view.dom.$tagFilterSelector,
        event: 'close',
        callback: this.onTagFilterSelectorClose,
      },
    ];
  }

  private onSelectionChangedEvent(event: WebComponentEvent) {
    this.selectedItemsData = event.detail.selectedValues;

    this.component.setAttribute(
      'value',
      this.selectedItemsData.length > 0 ? JSON.stringify(this.selectedItemsData) : ''
    );

    this.component.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          selectedValues: this.selectedItemsData,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private onTagFilterSelectorOpen(event: WebComponentEvent) {
    this.rerenderFilter().then().catch();
    this.component.dispatchEvent(new CustomEvent('gz-tag-filter-opened', { bubbles: true, composed: true }));
  }

  private async rerenderFilter() {
    // render not triggered from outside the component
    await super.render();
  }

  private onTagFilterSelectorClose(event: WebComponentEvent) {
    this.component.dispatchEvent(new CustomEvent('gz-tag-filter-closed', { bubbles: true, composed: true }));
  }

  public getSelectedItemsData() {
    return this.selectedItemsData;
  }

  public async setSelectedItemsData(selectedItems: Array<any>): Promise<void> {
    this.selectedItemsData = selectedItems;
    this.component.setAttribute(
      'value',
      this.selectedItemsData.length > 0 ? JSON.stringify(this.selectedItemsData) : ''
    );
    await this.render();
  }

  async getDataForView(): Promise<{
    gzTags: Array<TagItem>;
    selectedItems: Array<string>;
    shouldDisplayOptions?: boolean;
  }> {
    this.view.dom.$tagFilterSelector.isLoading = true;
    const shouldFetchData = this.component.shouldFetchData;

    const companyIdsArray: Array<string> = this.component.companyIds;

    if (this.isExternalRender) {
      // if rendering than read selected values from value attribute

      const attributeValue = this.component.getAttribute('value');
      let selectedItems = [];

      if (attributeValue !== null) {
        try {
          selectedItems = JSON.parse(attributeValue);
        } catch (error) {
          // ignore error
        }
      }
      const label = this.component.getAttribute('label');
      if (label) {
        this.view.dom.$tagFilterSelector.label = label;
      }
      const placeholder = this.component.getAttribute('placeholder');
      if (placeholder) {
        this.view.dom.$tagFilterSelector.placeholder = placeholder;
      }

      const labelWidth = this.component.getAttribute('labelWidth') as LabelWidthValues;
      if (labelWidth) {
        this.view.dom.$tagFilterSelector.labelWidth = labelWidth;
      }
      this.selectedItemsData = selectedItems;
    }

    let gzTags = [] as Array<TagItem>;

    if (!this.isExternalRender || this.selectedItemsData.length !== 0) {
      try {
        // if not rendering or rendering with non-empty selection that read gz tags from db
        gzTags = shouldFetchData ? await this.dataRepository.getDataForView(companyIdsArray) : [];
      } catch (error) {
        console.error('Error loading tags:', error);
      }

      if (shouldFetchData && gzTags.length > 0) {
        gzTags.unshift({
          _id: ExcludedTagTypes.NO_TAGS_SELECTED.toString(), // used; sent to backend
          name: this.dependencies.translateFn('No tags assigned'),
          type: ExcludedTagTypes.NO_TAGS_SELECTED, // not used
          description: '', // not used
          created: '', // not used
          company: {
            // not used
            id: '',
            name: '',
          },
        });

        const companyNamesArray = this.component.companyNames;
        for (const companyName of companyNamesArray) {
          let companyHasTags = false;

          for (const gzTag of gzTags) {
            companyHasTags = companyHasTags || gzTag.company.name === companyName;
          }

          if (!companyHasTags) {
            // we need to create a fake item, that we won't render, in order to create an empty group for the company
            gzTags.push({
              _id: ItemType.FAKE.toString(), // used
              name: '', // not used,
              type: ItemType.FAKE,
              description: '', // not used
              created: '', // not used
              company: {
                id: '', // not used
                name: companyName, // used to create an empty group for this company
              },
            });
          }
        }
      }
    }

    this.isExternalRender = false;

    const gzTagIds = Object.fromEntries(gzTags.map(gzTag => [gzTag._id, true]));
    this.selectedItemsData = this.selectedItemsData.filter(selectedItem => gzTagIds[selectedItem]);

    this.component.setAttribute(
      'value',
      this.selectedItemsData.length > 0 ? JSON.stringify(this.selectedItemsData) : ''
    );

    // dispatch event to ExtJS
    this.component.dispatchEvent(
      new CustomEvent('change', {
        detail: {
          selectedValues: this.selectedItemsData,
        },
        bubbles: true,
        composed: true,
      })
    );

    this.view.dom.$tagFilterSelector.isLoading = false;

    return { gzTags: gzTags, selectedItems: this.selectedItemsData, shouldDisplayOptions: shouldFetchData };
  }

  public closeFilter() {
    if (this.view?.dom?.$tagFilterSelector?.dropdownMenu?.open) {
      this.view?.dom?.$tagFilterSelector?.dropdownMenu?.closeDropdownMenu();
    }
  }

  async render() {
    this.view.shouldUsePluralOnEmptyStateText = this.component.shouldUsePluralOnEmptyStateText;
    this.closeFilter();
    this.isExternalRender = true;
    super.render();
  }
}
